import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";

const PRINCIPLES = [
    {
        n: "I.",
        title: "Ejecutamos, no observamos.",
        copy: "Nuestro equipo ha construido y escalado organizaciones comerciales en empresas como la tuya. Trabajamos hombro con hombro con la dirección y demostramos valor con resultados, no con presentaciones.",
    },
    {
        n: "II.",
        title: "Talento senior, modelo flexible.",
        copy: "Cada colaboración la lidera un socio con más de una década de experiencia en ventas B2B y transformación comercial. Acompañamiento directo, sin niveles ocultos ni subcontratación.",
    },
    {
        n: "III.",
        title: "Primero el negocio, después la tecnología.",
        copy: "Definimos la hipótesis de crecimiento antes que cualquier herramienta. La tecnología se incorpora solo cuando existe un caso claro de impacto en ingresos, eficiencia o productividad.",
    },
    {
        n: "IV.",
        title: "Tu autonomía es nuestro objetivo.",
        copy: "Diseñamos cada colaboración para que tu equipo gane capacidad propia. Construimos organizaciones comerciales que crecen por sí mismas: nuestro éxito es que dejes de necesitarnos.",
    },
];

const TIMELINE = [
    { p: "Semana 0", t: "Calibrar", c: "Mapeo de interlocutores clave, hipótesis de ingresos y criterios de éxito." },
    { p: "Semanas 1–3", t: "Diagnosticar", c: "Auditoría de datos, flujos de trabajo y capacidades. Primer borrador de la hoja de ruta." },
    { p: "Semanas 4–11", t: "Construir", c: "Primer sistema en producción. Rituales de adopción instalados." },
    { p: "Trimestral", t: "Acumular", c: "Revisión del portafolio, priorización, medición y gobierno." },
];

export default function About() {
    useReveal();
    return (
        <div data-testid="about-page">
            {/* Hero */}
            <section className="container-x pt-16 pb-24 lg:pt-24 lg:pb-32 border-b border-brand-line">
                <div className="reveal max-w-5xl">
                    <div className="label-mono">Nosotros · La firma</div>
                    <h1 className="mt-8 font-heading text-4xl sm:text-5xl lg:text-[76px] leading-[1.02] tracking-tighter">
                        Construimos el motor de crecimiento de tu empresa con
                        <span className="italic text-brand-electric"> experiencia humana</span> e Inteligencia Artificial.
                    </h1>
                    <p className="mt-10 font-heading text-3xl sm:text-4xl lg:text-5xl text-brand-navy leading-[1.1] tracking-tight max-w-4xl">
                        Nuestra experiencia lidera. <br />
                        <span className="italic text-brand-electric">La inteligencia artificial la potencia.</span>
                    </p>
                    <p className="mt-8 max-w-2xl text-lg lg:text-xl text-brand-gray leading-relaxed">
                        En Scalexa Group, el conocimiento, el criterio y las
                        decisiones permanecen en manos de expertos. Utilizamos la IA
                        para ampliar nuestras capacidades, acelerar procesos y
                        alcanzar mejores resultados.
                    </p>
                </div>
            </section>

            {/* Mission */}
            <section className="container-x py-24 lg:py-32" data-testid="about-mission">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 reveal">
                    <div className="lg:col-span-4">
                        <div className="label-mono">Misión</div>
                        <h2 className="font-heading text-3xl lg:text-4xl mt-6 leading-tight">
                            Construir ingresos, <br />
                            no narrativa.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 lg:col-start-6">
                        <p className="text-brand-gray text-lg leading-relaxed">
                            La mayoría de las empresas no tienen un problema de IA.
                            Tienen un problema de ejecución comercial, datos
                            fragmentados, embudos con fugas, operaciones manuales,
                            dashboards en los que nadie confía.
                        </p>
                        <p className="mt-6 text-brand-gray text-lg leading-relaxed">
                            Scalexa es la firma de operadores que rediseña ese motor
                            y usa la IA para que el crecimiento se acumule. Trabajamos
                            con empresas B2B entre la Serie B y la salida a bolsa,
                            anclando cada colaboración a una sola pregunta:
                            <em> ¿cómo va a mover esto el pipeline, la conversión, la retención o la velocidad?</em>
                        </p>
                    </div>
                </div>
            </section>

            {/* Misión · Visión · Propósito */}
            <section className="border-t border-brand-line" data-testid="about-mvp">
                <div className="container-x py-24 lg:py-32">
                    <div className="reveal mb-16 max-w-3xl">
                        <div className="label-mono">Propuesta</div>
                        <h2 className="font-heading text-4xl lg:text-5xl mt-6 leading-tight">
                            Por qué existimos, <br />
                            <span className="italic text-brand-electric">hacia dónde vamos.</span>
                        </h2>
                    </div>

                    <div className="border-t border-brand-line">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 py-12 border-b border-brand-line reveal" data-testid="about-mision">
                            <div className="lg:col-span-3">
                                <div className="font-mono text-brand-electric text-sm tracking-widest">01</div>
                                <h3 className="font-heading text-3xl mt-4">Misión</h3>
                            </div>
                            <div className="lg:col-span-8 lg:col-start-5 space-y-5 text-brand-gray text-lg leading-relaxed">
                                <p>
                                    Ayudar a empresas B2B a crecer de forma rentable
                                    mediante la optimización de procesos, la
                                    automatización inteligente y soluciones de
                                    inteligencia artificial supervisadas por personas.
                                </p>
                                <p>
                                    Combinamos más de una década de experiencia en
                                    desarrollo de negocios, ventas y optimización
                                    operativa para diseñar e implementar soluciones que
                                    reducen la carga operativa, incrementan la
                                    productividad y generan un impacto tangible en los
                                    ingresos de nuestros clientes. Creemos que la
                                    tecnología solo crea valor cuando está alineada con
                                    la estrategia del negocio y respaldada por el
                                    criterio humano.
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 py-12 border-b border-brand-line reveal" data-testid="about-vision">
                            <div className="lg:col-span-3">
                                <div className="font-mono text-brand-electric text-sm tracking-widest">02</div>
                                <h3 className="font-heading text-3xl mt-4">Visión</h3>
                            </div>
                            <div className="lg:col-span-8 lg:col-start-5 space-y-5 text-brand-gray text-lg leading-relaxed">
                                <p>
                                    Ser la empresa de referencia en la transformación
                                    inteligente de organizaciones, impulsando un futuro
                                    donde la inteligencia artificial y el talento humano
                                    trabajen en armonía para construir empresas más
                                    eficientes, competitivas y sostenibles.
                                </p>
                                <p>
                                    Aspiramos a liderar una nueva forma de hacer
                                    negocios, donde la automatización no sustituya a las
                                    personas, sino que potencie su capacidad para
                                    innovar, decidir y crear valor.
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 py-12 border-b border-brand-line reveal" data-testid="about-proposito">
                            <div className="lg:col-span-3">
                                <div className="font-mono text-brand-electric text-sm tracking-widest">03</div>
                                <h3 className="font-heading text-3xl mt-4">Propósito</h3>
                            </div>
                            <div className="lg:col-span-8 lg:col-start-5">
                                <p className="font-heading text-2xl lg:text-3xl text-brand-navy leading-tight">
                                    Creemos que la inteligencia artificial debe ampliar
                                    el <span className="italic text-brand-electric">potencial humano</span>, no reemplazarlo.
                                </p>
                                <p className="mt-6 text-brand-gray text-lg leading-relaxed">
                                    Existimos para ayudar a las empresas a adoptar la IA
                                    de forma responsable, estratégica y centrada en las
                                    personas, liberando tiempo para que los equipos
                                    puedan enfocarse en aquello que realmente genera
                                    creatividad, crecimiento e impacto.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Principles */}
            <section className="border-y border-brand-line bg-brand-soft">
                <div className="container-x py-24 lg:py-32" data-testid="about-principles">
                    <div className="reveal mb-16">
                        <div className="label-mono">Principios</div>
                        <h2 className="font-heading text-4xl lg:text-5xl mt-6">Cuatro convicciones que no negociamos.</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 border-t border-brand-line">
                        {PRINCIPLES.map((p, i) => (
                            <div
                                key={i}
                                className={`p-10 border-b border-brand-line ${
                                    i % 2 === 0 ? "md:border-r" : ""
                                } reveal`}
                                data-testid={`principle-${i}`}
                            >
                                <div className="font-mono text-brand-electric text-sm tracking-widest">
                                    {p.n}
                                </div>
                                <h3 className="font-heading text-2xl lg:text-3xl mt-4 leading-tight">
                                    {p.title}
                                </h3>
                                <p className="mt-4 text-brand-gray leading-relaxed">{p.copy}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Approach timeline */}
            <section className="container-x py-24 lg:py-32" data-testid="about-approach">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 reveal">
                    <div className="lg:col-span-4">
                        <div className="label-mono">Método</div>
                        <h2 className="font-heading text-3xl lg:text-4xl mt-6 leading-tight">
                            Un ritmo de trabajo, no una presentación.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 lg:col-start-6 space-y-px">
                        {TIMELINE.map((t, i) => (
                            <div
                                key={i}
                                className="grid grid-cols-12 gap-6 py-6 border-t border-brand-line"
                                data-testid={`timeline-${i}`}
                            >
                                <div className="col-span-3 label-mono pt-1">{t.p}</div>
                                <div className="col-span-3 font-heading text-xl text-brand-navy">
                                    {t.t}
                                </div>
                                <div className="col-span-6 text-brand-gray">{t.c}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="border-t border-brand-line bg-brand-navy text-white">
                <div className="container-x py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end reveal">
                    <div className="lg:col-span-8">
                        <div className="label-mono !text-white/60">Colabora</div>
                        <h2 className="font-heading text-4xl lg:text-6xl mt-6 leading-[0.98] text-white">
                            Si has llegado hasta aquí, <br />
                            <span className="italic">probablemente deberíamos hablar.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-4 lg:text-right">
                        <Link
                            to="/contact"
                            data-testid="about-cta-contact"
                            className="inline-flex items-center justify-center gap-2 bg-white text-brand-navy px-7 py-4 font-medium hover:bg-brand-electric hover:text-white transition-colors"
                        >
                            Inicia una colaboración <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
